import { inject, watch, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Capacitor } from '@capacitor/core'
import liveActivity from '@/lib/native/liveActivity'
import useUsers from '@/lib/hooks/use-users'
import { convertDistance, convertSpeed } from '@/lib/helpers/convert'
import tailwind from '@/lib/tailwind'
import { buildLiveActivityStep } from '@/lib/helpers/live-activity-step'

/**
 * Drives the native Live Activity (iOS) / ongoing notification (Android) that
 * mirrors the recording screen: chrono + distance + speed + heart rate.
 *
 * Values are pre-formatted here (unit preferences + i18n) and handed to native,
 * which renders the chrono itself from `startedAt` (self-ticking, no per-second push).
 *
 * Call once from the app root (App.vue) so it spans the whole recording no matter
 * how the route changes. Safe no-op on web / unavailable platforms.
 */
export default function useRecordingLiveActivity() {
    if (!liveActivity.isAvailable()) return

    const movepro = inject('movepro')
    const heartrate = inject('heartrate')
    const { userUnits } = useUsers()
    const { t } = useI18n()

    let started = false
    let lifecycleOperation = Promise.resolve()

    const queueLifecycleOperation = (operation) => {
        lifecycleOperation = lifecycleOperation.then(operation, operation)
        return lifecycleOperation
    }

    const isSessionActive = () => !!movepro.current.liveSession.startedAt && !movepro.current.liveSession.endedAt

    const labels = () => ({
        title: t('record.live-activity-title'),
        paused: t('record.live-activity-paused'),
        distance: t('metric.distance'),
        speed: t('metric.speed'),
        heartRate: t('sensor.heartrate'),
        step: t('record.live-activity-step'),
        next: t('program.next'),
    })

    // The protocol step, when the session follows one; {} otherwise so the
    // native side simply draws no step row.
    const step = () =>
        buildLiveActivityStep(movepro.current.liveSession.protocol, {
            label: (gait) => t(`gait.${gait}`),
            colours: tailwind.theme.color.gaits,
        })

    const currentBpm = () => {
        const connected = heartrate?.isConnected?.value
        const bpm = heartrate?.properties?.heartrate
        return connected && bpm ? Math.round(bpm) : null
    }

    // Mirror Live.vue: "-" when the sensor is offline, the localized no-gps text
    // when connected without a fix, otherwise the converted value + unit.
    const metric = (kind) => {
        const current = movepro.current
        if (!current.isConnected) return '-'
        if (!current.hasGpsSignal) return t('sensor.no-gps')

        if (kind === 'distance') {
            const d = convertDistance(current.liveDistance, 'm', userUnits.value.distance)
            return `${d.value} ${d.unit}`
        }

        const s = convertSpeed(current.liveSpeed, 'm/min', userUnits.value.speed)
        return `${s.value} ${s.unit}`
    }

    // Time already spent in completed pauses, so native can tick from an
    // "effective start" that matches liveDuration (which subtracts pauses).
    const pausedMs = (session) => (session.pauses || []).reduce((sum, p) => (p?.to ? sum + (p.to - p.from) : sum), 0)

    const buildData = () => {
        const session = movepro.current.liveSession
        return {
            startedAt: session.startedAt ? session.startedAt + pausedMs(session) : session.startedAt,
            paused: !!session.paused,
            distance: metric('distance'),
            speed: metric('speed'),
            bpm: currentBpm(),
            labels: labels(),
            ...step(),
        }
    }

    const startActivity = () =>
        queueLifecycleOperation(async () => {
            if (started || !isSessionActive()) return
            const result = await liveActivity.start(buildData())
            // Roll back so we never spam update() if the native start failed.
            started = !!result
        })

    const endActivity = () =>
        queueLifecycleOperation(async () => {
            if (!started) return
            started = false
            await liveActivity.end()
        })

    const updateActivity = () => {
        if (!started) return
        liveActivity.update(buildData())
    }

    // iOS purges orphaned activities when the native plugin loads. Android's
    // foreground service needs the equivalent cleanup here, before any start.
    if (Capacitor.getPlatform() === 'android') {
        queueLifecycleOperation(() => liveActivity.end())
    }

    // Start and end only on real active-state transitions. In particular, the
    // initial empty state must not send an unconditional native end request.
    watch(
        isSessionActive,
        (active, wasActive) => {
            if (active) startActivity()
            else if (wasActive) endActivity()
        },
        { immediate: true }
    )

    // UPDATE — piggyback on the existing 1s tick that feeds the on-screen chrono.
    watch(
        () => movepro.current.liveSession.now.time,
        () => updateActivity()
    )

    // UPDATE immediately on pause / resume (the 1s tick is cleared while paused).
    watch(
        () => movepro.current.liveSession.paused,
        () => updateActivity()
    )

    // UPDATE when the protocol moves to another gait, or ends: the countdown
    // restarts from the new step at once rather than on the next tick.
    watch(
        () => [movepro.current.liveSession.protocol?.gait, movepro.current.liveSession.protocol?.position],
        () => updateActivity()
    )

    // Safety net: never leave a stale activity if the page unmounts mid-session.
    onUnmounted(() => {
        endActivity()
    })
}
